import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLayers } from '@agissc/ktag-map';
import isDeepEqual from 'fast-deep-equal';
import useAppState from '../../AppStore';
import { useMetadataQueryStartupMessage } from '../mapContent/api/useMetadataQuery';

interface StartUpNotificationProps {
  topicLayer: string;
  title: string;
}

const StartupNotification = ({ topicLayer, title }: StartUpNotificationProps) => {
  const notifications = useAppState((state) => state.notifications);
  const addNotification = useAppState((state) => state.addNotification);
  const removeNotification = useAppState((state) => state.removeNotification);

  const metadata = useMetadataQueryStartupMessage(topicLayer);

  const hasNotification = (search: string) =>
    notifications.some((n) => n.type === 'persistent' && n.id.includes(search));

  useEffect(() => {
    if (!metadata.data) return undefined;
    const id = `startup-${topicLayer}`;

    if (hasNotification(id)) return undefined;

    addNotification({
      id,
      title,
      message: metadata.data,
      type: 'persistent',
    });

    return () => removeNotification(id);
  }, [metadata.data]);

  return null;
};

interface AddRemoveLayerNotificationProps {
  topicLayer: string;
  topicLayerName: string;
  addNotification: (notification: any) => void;
  removeNotification: (id: string) => void;
}

const AddRemoveLayerNotification = ({
  topicLayer,
  topicLayerName,
  addNotification,
  removeNotification,
}: AddRemoveLayerNotificationProps) => {
  const { t } = useTranslation('agisviewer');

  useEffect(() => {
    removeNotification(`remove-layer-${topicLayer}`);

    addNotification({
      id: `add-layer-${topicLayer}`,
      title: t('notification_topic_add', { count: 1 }),
      message: t('notification_topic_name_add', {
        topicLayerName,
      }),
      type: 'information',
    });

    return () => {
      removeNotification(`add-layer-${topicLayer}`);

      addNotification({
        id: `remove-layer-${topicLayer}`,
        title: t('notification_topic_remove', { count: 1 }),
        message: t('notification_topic_name_remove', {
          topicLayerName,
        }),
        type: 'information',
      });
    };
  }, []);

  return null;
};

const TopicLayerNotifications = () => {
  const { t } = useTranslation('agisviewer');

  const selectedTopicLayers = useAppState((state) => state.selectedTopicLayers);
  const addNotification = useAppState((state) => state.addNotification);
  const removeNotification = useAppState((state) => state.removeNotification);
  const activeTool = useAppState((state) => state.activeTool);
  const displayResetNotification = useAppState((state) => state.displayResetNotification);

  const currentSelectedTopicLayers = useRef<string[]>(selectedTopicLayers);
  const prevSelectedTopicLayers = useRef<string[]>([]);
  const initialized = useRef(false);

  const { data: layersLookup, isLoading } = useLayers();

  const notify = useCallback(
    (notification: any, changes: number = 1) => {
      if (activeTool !== 'layerSelection') return;

      if (changes > 1) return;

      addNotification(notification);
    },
    [activeTool, addNotification],
  );

  //  Handles the notification for adding and removing multiple layers.
  useEffect(() => {
    if (displayResetNotification) {
      initialized.current = false;
      return;
    }

    if (isDeepEqual(selectedTopicLayers, currentSelectedTopicLayers.current)) return;

    const prev = currentSelectedTopicLayers.current.length;
    const next = selectedTopicLayers.length;

    const removed = prev - next;
    const added = next - prev;

    if (removed > 1) {
      removeNotification('add-all-layers');
      notify(
        {
          id: 'remove-all-layers',
          title: t('notification_topic_remove', { count: removed }),
          message: t('notification_topic_many_remove'),
          type: 'information',
        },
        removed,
      );
    }

    if (added > 1) {
      removeNotification('remove-all-layers');
      notify(
        {
          id: 'add-all-layers',
          title: t('notification_topic_add', { count: added }),
          message: t('notification_topic_many_add'),
          type: 'information',
        },
        added,
      );
    }
    prevSelectedTopicLayers.current = currentSelectedTopicLayers.current;
    currentSelectedTopicLayers.current = selectedTopicLayers;
    initialized.current = true;
  }, [displayResetNotification, selectedTopicLayers]);

  const notifySingle = useCallback(
    (notification: any) => {
      setTimeout(() => {
        if (!initialized.current) return;

        const diff = Math.abs(
          prevSelectedTopicLayers.current.length -
            currentSelectedTopicLayers.current.length,
        );

        notify(notification, diff);
      }, 0);
    },
    [notify],
  );

  if (isLoading) return null;

  return (
    <>
      {selectedTopicLayers.map((layer) => (
        <AddRemoveLayerNotification
          key={layer}
          topicLayer={layer}
          topicLayerName={layersLookup[layer]?.title ?? t('layer')}
          addNotification={notifySingle}
          removeNotification={removeNotification}
        />
      ))}
      {selectedTopicLayers
        .filter((layer) => layersLookup[layer]?.type === 'esriMapServer')
        .map((layer) => (
          <StartupNotification
            key={layer}
            topicLayer={layer}
            title={layersLookup[layer]?.title ?? layer}
          />
        ))}
    </>
  );
};

export default TopicLayerNotifications;
